/**
 * Nightwave — theme.js
 * Core utilities and global components (vanilla JS, no dependencies).
 *
 * - theme.utils      helpers (debounce, fetch config, announcements…)
 * - theme.events     tiny pub/sub on document (theme:* events)
 * - theme.dialogs    animated <dialog> drawers & modals
 * - Custom elements: sticky-header, announcement-bar, tabs-component,
 *   slider-component, quantity-input, predictive-search,
 *   product-recommendations, share-button
 */
(() => {
  'use strict';

  const theme = (window.theme = window.theme || {});
  theme.routes = theme.routes || {};
  theme.strings = theme.strings || {};
  theme.settings = theme.settings || {};

  const define = (name, constructor) => {
    if (!customElements.get(name)) customElements.define(name, constructor);
  };

  /* Utilities
     ======================================================================== */
  theme.utils = {
    debounce(callback, wait = 300) {
      let timeout;
      return (...args) => {
        clearTimeout(timeout);
        timeout = setTimeout(() => callback(...args), wait);
      };
    },

    fetchConfig(type = 'json') {
      return {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: `application/${type}`,
          'X-Requested-With': 'XMLHttpRequest',
        },
      };
    },

    parseHTML(text) {
      return new DOMParser().parseFromString(text, 'text/html');
    },

    announce(message) {
      const region = document.querySelector('[data-live-region]');
      if (!region || !message) return;
      region.textContent = '';
      setTimeout(() => {
        region.textContent = message;
      }, 60);
    },

    prefersReducedMotion() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    },

    formatTime(seconds) {
      if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
      const minutes = Math.floor(seconds / 60);
      const rest = Math.floor(seconds % 60);
      return `${minutes}:${String(rest).padStart(2, '0')}`;
    },
  };

  theme.events = {
    on(name, callback) {
      document.addEventListener(`theme:${name}`, callback);
    },
    off(name, callback) {
      document.removeEventListener(`theme:${name}`, callback);
    },
    emit(name, detail = {}) {
      document.dispatchEvent(new CustomEvent(`theme:${name}`, { detail }));
    },
  };

  /* Dialogs (menu drawer, search, cart drawer)
     Uses the native <dialog> element: focus trap, Escape and top layer for free.
     ======================================================================== */
  theme.dialogs = {
    open(dialog, trigger) {
      if (!dialog || dialog.open || typeof dialog.showModal !== 'function') return;
      dialog._trigger = trigger || document.activeElement;
      dialog.classList.remove('is-closing');
      dialog.showModal();
      requestAnimationFrame(() => dialog.classList.add('is-open'));
      theme.events.emit('dialog:open', { dialog });
    },

    close(dialog) {
      if (!dialog || !dialog.open || dialog.classList.contains('is-closing')) return;
      dialog.classList.remove('is-open');
      dialog.classList.add('is-closing');

      const finish = () => {
        dialog.classList.remove('is-closing');
        dialog.close();
        const trigger = dialog._trigger;
        if (trigger && document.contains(trigger) && typeof trigger.focus === 'function') {
          trigger.focus({ preventScroll: true });
        }
        theme.events.emit('dialog:close', { dialog });
      };

      if (theme.utils.prefersReducedMotion()) finish();
      else setTimeout(finish, 400);
    },
  };

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-open-dialog]');
    if (opener) {
      const dialog = document.getElementById(opener.dataset.openDialog);
      if (dialog && typeof dialog.showModal === 'function') {
        event.preventDefault();
        theme.dialogs.open(dialog, opener);
      }
      return;
    }

    const closer = event.target.closest('[data-close-dialog]');
    if (closer) {
      event.preventDefault();
      theme.dialogs.close(closer.closest('dialog'));
      return;
    }

    // Click on the backdrop (the dialog element itself, outside its box)
    const target = event.target;
    if (target instanceof HTMLDialogElement && target.hasAttribute('data-dialog') && event.detail > 0) {
      const rect = target.getBoundingClientRect();
      const inside =
        event.clientX >= rect.left && event.clientX <= rect.right && event.clientY >= rect.top && event.clientY <= rect.bottom;
      if (!inside) theme.dialogs.close(target);
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const dialogs = [...document.querySelectorAll('dialog[data-dialog][open]')];
    const dialog = dialogs[dialogs.length - 1];
    if (!dialog) return;
    event.preventDefault();
    theme.dialogs.close(dialog);
  });

  // Keep classes in sync if a dialog gets closed natively (e.g. repeated Escape)
  document.addEventListener(
    'close',
    (event) => {
      if (event.target instanceof HTMLDialogElement) event.target.classList.remove('is-open', 'is-closing');
    },
    true
  );

  // Escape key: animate the closing instead of the instant native close
  document.addEventListener(
    'cancel',
    (event) => {
      const dialog = event.target;
      if (dialog instanceof HTMLDialogElement && dialog.hasAttribute('data-dialog')) {
        event.preventDefault();
        theme.dialogs.close(dialog);
      }
    },
    true
  );

  /* Reveal on scroll
     ======================================================================== */
  const reveal = {
    observer: null,

    init(root = document) {
      const elements = root.querySelectorAll('[data-reveal]:not(.is-revealed)');
      const enabled =
        document.body.classList.contains('has-reveal') &&
        'IntersectionObserver' in window &&
        !(window.Shopify && window.Shopify.designMode);

      if (!enabled) {
        elements.forEach((element) => element.classList.add('is-revealed'));
        return;
      }

      if (!this.observer) {
        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              entry.target.classList.add('is-revealed');
              this.observer.unobserve(entry.target);
            });
          },
          { rootMargin: '0px 0px -6% 0px', threshold: 0.05 }
        );
      }

      elements.forEach((element) => this.observer.observe(element));
    },
  };
  theme.reveal = reveal;

  /* Sticky header
     ======================================================================== */
  class StickyHeader extends HTMLElement {
    connectedCallback() {
      this.section = this.closest('.section-header');
      this.mode = this.dataset.sticky;
      this.lastScrollY = window.scrollY;
      this.onScroll = this.onScroll.bind(this);

      this.resizeObserver = new ResizeObserver(() => this.updateHeight());
      this.resizeObserver.observe(this);
      this.updateHeight();

      if (this.mode !== 'none') window.addEventListener('scroll', this.onScroll, { passive: true });
      this.onScroll();
      this.initDropdowns();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      this.resizeObserver?.disconnect();
      document.removeEventListener('click', this.onOutsideClick);
      document.removeEventListener('keyup', this.onKeyUp);
    }

    updateHeight() {
      document.documentElement.style.setProperty('--header-height', `${this.offsetHeight}px`);
    }

    onScroll() {
      if (this.ticking) return;
      this.ticking = true;
      requestAnimationFrame(() => {
        const scrollY = window.scrollY;
        this.classList.toggle('is-scrolled', this.mode !== 'none' && scrollY > 8);

        if (this.mode === 'scroll-up' && this.section) {
          const goingDown = scrollY > this.lastScrollY && scrollY > this.offsetHeight * 2;
          const hasOpenDropdown = Boolean(this.querySelector('details[open]'));
          this.section.classList.toggle('is-hidden', goingDown && !hasOpenDropdown);
        }

        this.lastScrollY = scrollY;
        this.ticking = false;
      });
    }

    initDropdowns() {
      const hoverQuery = window.matchMedia('(hover: hover)');
      this.dropdowns = [...this.querySelectorAll('[data-header-dropdown]')];

      this.dropdowns.forEach((details) => {
        const summary = details.querySelector('summary');
        details.addEventListener('mouseenter', () => {
          if (hoverQuery.matches) details.open = true;
        });
        details.addEventListener('mouseleave', () => {
          if (hoverQuery.matches) details.open = false;
        });
        // Mouse clicks keep hover menus open; keyboard (detail === 0) toggles natively
        summary?.addEventListener('click', (event) => {
          if (hoverQuery.matches && event.detail > 0) {
            event.preventDefault();
            details.open = true;
          }
        });
        details.addEventListener('toggle', () => {
          if (!details.open) return;
          this.dropdowns.forEach((other) => {
            if (other !== details) other.open = false;
          });
        });
      });

      this.onOutsideClick = (event) => {
        this.dropdowns.forEach((details) => {
          if (details.open && !details.contains(event.target)) details.open = false;
        });
      };
      this.onKeyUp = (event) => {
        if (event.key !== 'Escape') return;
        this.dropdowns.forEach((details) => {
          if (!details.open) return;
          details.open = false;
          details.querySelector('summary')?.focus();
        });
      };
      document.addEventListener('click', this.onOutsideClick);
      document.addEventListener('keyup', this.onKeyUp);
    }
  }
  define('sticky-header', StickyHeader);

  /* Announcement bar
     ======================================================================== */
  class AnnouncementBar extends HTMLElement {
    connectedCallback() {
      this.messages = [...this.querySelectorAll('[data-announcement]')];
      this.index = Math.max(0, this.messages.findIndex((message) => message.classList.contains('is-active')));
      if (this.messages.length < 2 || this.dataset.autoplay !== 'true' || theme.utils.prefersReducedMotion()) return;

      this.addEventListener('mouseenter', () => (this.paused = true));
      this.addEventListener('mouseleave', () => (this.paused = false));
      this.addEventListener('focusin', () => (this.paused = true));
      this.addEventListener('focusout', () => (this.paused = false));
      this.timer = setInterval(() => {
        if (!this.paused) this.show((this.index + 1) % this.messages.length);
      }, Number(this.dataset.speed) || 5000);
    }

    disconnectedCallback() {
      clearInterval(this.timer);
    }

    show(index) {
      if (index < 0 || !this.messages[index]) return;
      this.messages[this.index]?.classList.remove('is-active');
      this.index = index;
      this.messages[index].classList.add('is-active');
    }
  }
  define('announcement-bar', AnnouncementBar);

  /* Tabs (featured beats pill switch)
     ======================================================================== */
  class TabsComponent extends HTMLElement {
    connectedCallback() {
      this.tabs = [...this.querySelectorAll('[data-tab]')];
      this.panels = [...this.querySelectorAll('[data-tab-panel]')];

      this.tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => this.select(index));
        tab.addEventListener('keydown', (event) => {
          const keys = { ArrowRight: index + 1, ArrowLeft: index - 1, Home: 0, End: this.tabs.length - 1 };
          if (!(event.key in keys)) return;
          event.preventDefault();
          const next = (keys[event.key] + this.tabs.length) % this.tabs.length;
          this.select(next, true);
        });
      });
    }

    select(index, focus = false) {
      this.tabs.forEach((tab, i) => {
        const selected = i === index;
        tab.setAttribute('aria-selected', String(selected));
        tab.tabIndex = selected ? 0 : -1;
      });

      this.panels.forEach((panel, i) => {
        const active = i === index;
        if (panel.hidden === !active) return;
        panel.hidden = !active;
        if (active && !theme.utils.prefersReducedMotion()) {
          panel.classList.remove('is-entering');
          void panel.offsetWidth;
          panel.classList.add('is-entering');
        }
      });

      if (focus) this.tabs[index]?.focus();
      theme.events.emit('tracks:changed');
    }
  }
  define('tabs-component', TabsComponent);

  /* Horizontal slider with arrow buttons
     ======================================================================== */
  class SliderComponent extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-slider-track]');
      this.prevButton = this.querySelector('[data-slider-prev]');
      this.nextButton = this.querySelector('[data-slider-next]');
      if (!this.track) return;

      this.update = this.update.bind(this);
      this.track.addEventListener('scroll', theme.utils.debounce(this.update, 50), { passive: true });
      this.prevButton?.addEventListener('click', () => this.scrollPage(-1));
      this.nextButton?.addEventListener('click', () => this.scrollPage(1));
      this.resizeObserver = new ResizeObserver(this.update);
      this.resizeObserver.observe(this.track);
      this.update();
    }

    disconnectedCallback() {
      this.resizeObserver?.disconnect();
    }

    scrollPage(direction) {
      this.track.scrollBy({ left: direction * this.track.clientWidth * 0.9, behavior: 'smooth' });
    }

    update() {
      const maxScroll = this.track.scrollWidth - this.track.clientWidth - 2;
      if (this.prevButton) this.prevButton.disabled = this.track.scrollLeft <= 2;
      if (this.nextButton) this.nextButton.disabled = this.track.scrollLeft >= maxScroll;
    }
  }
  define('slider-component', SliderComponent);

  /* Quantity input (+ / −)
     ======================================================================== */
  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      this.querySelectorAll('button').forEach((button) => {
        button.addEventListener('click', (event) => {
          event.preventDefault();
          const previous = this.input.value;
          const min = Number(this.input.min || 0);
          const value = (parseInt(this.input.value, 10) || 0) + (button.name === 'plus' ? 1 : -1);
          this.input.value = String(Math.max(min, value));
          if (previous !== this.input.value) this.input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });
    }
  }
  define('quantity-input', QuantityInput);

  /* Predictive search
     ======================================================================== */
  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[type="search"]');
      this.results = this.querySelector('[data-predictive-results]');
      this.cache = new Map();
      if (!this.input || !this.results) return;

      this.input.form?.addEventListener('submit', (event) => {
        if (!this.input.value.trim()) event.preventDefault();
      });

      if (this.dataset.enabled !== 'true' || !theme.routes.predictiveSearch) return;
      this.input.addEventListener(
        'input',
        theme.utils.debounce(() => this.search(this.input.value.trim()), 250)
      );
    }

    async search(term) {
      if (!term) {
        this.render('');
        return;
      }
      if (this.cache.has(term)) {
        this.render(this.cache.get(term));
        return;
      }

      this.abortController?.abort();
      this.abortController = new AbortController();

      const params = new URLSearchParams({
        q: term,
        section_id: 'predictive-search',
        'resources[type]': 'product,collection,page,article',
        'resources[limit]': '6',
        'resources[limit_scope]': 'each',
        'resources[options][unavailable_products]': 'last',
      });

      try {
        const response = await fetch(`${theme.routes.predictiveSearch}?${params}`, {
          signal: this.abortController.signal,
        });
        if (!response.ok) throw new Error(String(response.status));
        const panel = theme.utils.parseHTML(await response.text()).querySelector('#PredictiveSearchResultsPanel');
        const html = panel ? panel.outerHTML : '';
        this.cache.set(term, html);
        this.render(html);
      } catch (error) {
        if (error.name !== 'AbortError') this.render('');
      }
    }

    render(html) {
      this.results.innerHTML = html;
      this.results.hidden = !html;
      theme.events.emit('tracks:changed');
    }
  }
  define('predictive-search', PredictiveSearch);

  /* Product recommendations (related beats)
     ======================================================================== */
  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      if (!this.dataset.url || this.loaded) return;

      const load = async () => {
        this.loaded = true;
        try {
          const response = await fetch(this.dataset.url);
          if (!response.ok) return;
          const fresh = theme.utils.parseHTML(await response.text()).querySelector('product-recommendations');
          if (fresh && fresh.querySelector('.grid__item')) {
            this.innerHTML = fresh.innerHTML;
            theme.events.emit('tracks:changed');
          }
        } catch (error) {
          /* Keep the server-rendered fallback */
        }
      };

      if (!('IntersectionObserver' in window)) {
        load();
        return;
      }
      const observer = new IntersectionObserver(
        (entries) => {
          if (!entries[0].isIntersecting) return;
          observer.disconnect();
          load();
        },
        { rootMargin: '0px 0px 400px 0px' }
      );
      observer.observe(this);
    }
  }
  define('product-recommendations', ProductRecommendations);

  /* Share button (Web Share API, clipboard fallback)
     ======================================================================== */
  class ShareButton extends HTMLElement {
    connectedCallback() {
      this.querySelector('[data-share]')?.addEventListener('click', () => this.share());
    }

    async share() {
      const data = { title: this.dataset.title, url: this.dataset.url };
      if (navigator.share) {
        try {
          await navigator.share(data);
        } catch (error) {
          /* Share sheet dismissed */
        }
        return;
      }
      try {
        await navigator.clipboard.writeText(data.url);
        const label = this.querySelector('[data-share-label]');
        const copied = this.querySelector('[data-share-copied]');
        if (!label || !copied) return;
        label.hidden = true;
        copied.hidden = false;
        theme.utils.announce(copied.textContent);
        setTimeout(() => {
          label.hidden = false;
          copied.hidden = true;
        }, 2500);
      } catch (error) {
        /* Clipboard unavailable */
      }
    }
  }
  define('share-button', ShareButton);

  /* Misc: auto-submit selects, contact prefill, theme editor
     ======================================================================== */
  document.addEventListener('change', (event) => {
    const select = event.target.closest('[data-auto-submit]');
    if (select && select.form) select.form.submit();
  });

  const prefillFields = () => {
    const params = new URLSearchParams(window.location.search);
    document.querySelectorAll('[data-prefill-param]').forEach((input) => {
      const value = params.get(input.dataset.prefillParam);
      if (value && !input.value) input.value = value;
    });
  };

  document.addEventListener('shopify:section:load', (event) => {
    reveal.init(event.target);
    theme.events.emit('tracks:changed');
  });

  document.addEventListener('shopify:block:select', (event) => {
    const block = event.target;
    const tab = block.closest && block.closest('[data-tab]');
    if (tab) {
      const tabs = tab.closest('tabs-component');
      tabs?.select(tabs.tabs.indexOf(tab));
    }
    const message = block.closest && block.closest('[data-announcement]');
    if (message) {
      const bar = message.closest('announcement-bar');
      bar?.show(bar.messages.indexOf(message));
    }
  });

  const init = () => {
    reveal.init();
    prefillFields();
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
